import { useEffect } from 'react';
import { bt, btp } from '../i18n';
import { dismissMediaUpload, useMediaUploadList, useMediaUploadsShown } from '../lib/mediaUploads';
import { formatElapsed, useElapsedSeconds } from '../lib/elapsed';

/**
 * The topbar's BACKGROUND-UPLOAD line (founder, 2026-09-15).
 *
 * An upload that survives its panel is only half the promise: if nothing on screen says so, the seller
 * still believes the file was lost the moment they pressed ×. This is the other half — the one place that
 * is always visible, whatever panel is open or closed, saying what is still on its way.
 *
 * It also carries the case that used to disappear entirely: an upload that FAILS with its panel closed.
 * Before, that set state on an unmounted component, i.e. nothing at all — no image, no message, nothing to
 * retry. Here it stays, names the file, and offers the two moves that follow: go back to the field, or
 * re-send the bytes we still hold.
 *
 * Renders NOTHING when there is nothing in flight — the topbar must not grow a permanent empty slot.
 *
 * And nothing either when the open panel is already saying it (founder, 2026-09-16: "pas besoin d'écrire
 * la sauvegarde dans le header puisqu'on l'écrit déjà dans l'image qui se télécharge"). This line is the
 * SUBSTITUTE for the panel's own bar, not a second copy of it: with the field on screen, the seller reads
 * the percentage over their own picture, where the Cancel button is. The tray takes over the instant that
 * control unmounts — the panel closes, another section is opened — which is the only case it was built for.
 */
export function UploadTray() {
  const all = useMediaUploadList();
  const shown = useMediaUploadsShown();
  // A job whose own control is mounted is left to that control, entirely: its progress AND its failure.
  const jobs = all.filter((j) => !shown[j.key]);
  const failed = jobs.filter((j) => j.phase === 'error');
  const sending = jobs.filter((j) => j.phase !== 'error');
  // Counted from the OLDEST job in flight — the seller's question is "how long have I been waiting",
  // not "how long has each file been waiting". Hooks run before the early returns below.
  const oldest = sending.length > 0 ? Math.min(...sending.map((j) => j.startedAt)) : undefined;
  const elapsed = useElapsedSeconds(oldest);

  // Closing the tab kills an XHR the way nothing inside the app can. The seller gets the browser's own
  // "leave site?" prompt — the text is the browser's, we only ask for it, and only while bytes are moving.
  // Counted over ALL jobs, not the ones this tray draws: an upload the open panel is showing is just as
  // dead when the tab goes, so the warning cannot depend on which component happens to be painting it.
  const inFlight = all.some((j) => j.phase !== 'error');
  useEffect(() => {
    if (!inFlight) return;
    const warn = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [inFlight]);

  if (jobs.length === 0) return null;

  if (sending.length > 0) {
    // One number for the whole batch: the seller cares how far off "done" is, not how each file is doing.
    const pct = Math.round(sending.reduce((sum, j) => sum + j.percent, 0) / sending.length);
    // …but only once the transport has actually measured something. `upload.onprogress` is not guaranteed
    // to fire, and a determinate "0 %" sitting in the topbar for a minute is the same lie as the "100 %"
    // this work removed. With nothing measured, the file name and the clock say all we truthfully know.
    const measured = sending.some((j) => j.reported);
    return (
      <span className="bld-uploads" role="status" aria-live="polite">
        <span className="bld-uploads__spinner" aria-hidden="true" />
        {measured ? (
          sending.length === 1 ? (
            btp('chrome.media.bg_one', 'Sending {name}… {pct}%', {
              name: sending[0]!.fileName,
              pct,
            })
          ) : (
            btp('chrome.media.bg_many', 'Sending {n} files… {pct}%', { n: sending.length, pct })
          )
        ) : (
          <>
            {bt('chrome.media.uploading', 'Uploading…')}
            {/* The file name is a proper noun, not a phrase — its own element rather than a second
                sentence to translate, which also keeps this change out of seven locale files. */}
            {sending.length === 1 && (
              <span className="bld-uploads__name">{sending[0]!.fileName}</span>
            )}
          </>
        )}
        {/* Always true, always moving — the percentage alone left the founder staring at a frozen
            "100 %" for minutes (see `lib/elapsed.ts`). Its own element: never inside a translated
            sentence, so no language has its word order decided for it here. */}
        {elapsed > 0 && <span className="bld-uploads__time">{formatElapsed(elapsed)}</span>}
      </span>
    );
  }

  const first = failed[0]!;
  return (
    <span className="bld-uploads bld-uploads--error" role="status" aria-live="assertive">
      {failed.length === 1
        ? btp('chrome.media.bg_failed_one', 'Upload failed: {name}', { name: first.fileName })
        : btp('chrome.media.bg_failed_many', '{n} uploads failed', { n: failed.length })}
      {/* WHY the reason is here (founder, 2026-09-18: "on doit pas avoir une err comme ça").
          The line above names the FILE and stops. The cause — too large, wrong type, too many uploads,
          the connection gave out at 40 % — is already in the job, in the seller's language, and it was
          painted in ONE place: the panel of the very field that failed. With that panel closed, which is
          the only situation this tray exists for, a red banner sat in the topbar saying a file had failed
          and nothing else, for as long as it took someone to dismiss it. Its own element, never inside a
          translated sentence: the message is a whole sentence already, in whatever language it was
          phrased in. */}
      {first.error && (
        <span className="bld-uploads__why" title={first.error}>
          {first.error}
        </span>
      )}
      {first.reveal && (
        <button type="button" className="bld-uploads__act" onClick={() => first.reveal?.()}>
          {bt('chrome.media.show', 'Show')}
        </button>
      )}
      <button type="button" className="bld-uploads__act" onClick={() => first.retry()}>
        {bt('chrome.media.try_again', 'Try again')}
      </button>
      <button
        type="button"
        className="bld-uploads__act"
        onClick={() => dismissMediaUpload(first.key)}
      >
        {bt('chrome.media.dismiss', 'Dismiss')}
      </button>
    </span>
  );
}
